/**
 * CustomerMutation.ts
 *
 * Interfaces for customer-level mutation operations.
 */

// ============================================================================
// UPDATE STATEMENT DELIVERY TYPE
// ============================================================================

/**
 * Enum matching the StatementMailingDeliveryType GraphQL enum.
 * EMAIL_DISPATCH = email delivery, PAPER_DISPATCH = postal mail, NONE = no delivery
 */
export enum StatementMailingDeliveryType {
	EMAIL_DISPATCH = 'EMAIL_DISPATCH',
	PAPER_DISPATCH = 'PAPER_DISPATCH',
	NONE = 'NONE',
}

/**
 * Input variables for updateStatementDelivery mutation.
 */
export interface UpdateStatementDeliveryInput {
	customerId: number
	deliveryType: StatementMailingDeliveryType
}

/**
 * Response wrapper for updateStatementDelivery mutation.
 */
export interface UpdateStatementDeliveryResponse {
	updateStatementDelivery: {
		statementDelivery: {
			deliveryType: {
				id: number
				name: string
			}
		}
	}
}

// ============================================================================
// UPDATE CUSTOMER FROZEN STATUS
// ============================================================================

/**
 * Input variables for updateCustomerFrozen mutation.
 * @param id     - The customer's numeric ID (currentCustomer.id)
 * @param is     - true = freeze the account, false = unfreeze
 * @param reason - Human-readable reason for the status change
 */
export interface UpdateCustomerFrozenInput {
	id: number
	is: boolean
	reason: string
}

/**
 * The customer subset returned by updateCustomerFrozen.
 */
export interface FrozenCustomerResult {
	customerNumber: number
	id: number
	isCompany: boolean
	lastName: string
	name: string
}

/**
 * Response wrapper for updateCustomerFrozen mutation.
 */
export interface UpdateCustomerFrozenResponse {
	updateCustomerFrozen: {
		customer: FrozenCustomerResult
	}
}

// ============================================================================
// UPDATE INVOICE DELIVERY TYPE
// ============================================================================

/**
 * Enum matching the InvoiceMailingDeliveryType GraphQL enum.
 * EMAIL_DISPATCH = email delivery, PAPER_DISPATCH = postal mail, NONE = no delivery
 */
export enum InvoiceMailingDeliveryType {
	E_INVOICE = 'E_INVOICE',
	EMAIL_DISPATCH = 'EMAIL_DISPATCH',
	NONE = 'NONE',
	PAPER_DISPATCH = 'PAPER_DISPATCH',
}

export interface UpdateInvoiceDeliveryInput {
	customerId: number
	deliveryType: InvoiceMailingDeliveryType
}

export interface UpdateInvoiceDeliveryResponse {
	updateInvoiceDelivery: {
		invoiceDelivery: {
			deliveryType: {
				id: number
				name: string
			}
		}
	}
}

// ============================================================================
// UPDATE AUTOGIRO
// ============================================================================

/**
 * Input variables for updateAutogiro mutation.
 * All fields are optional — only pass what you want to change.
 */
export interface UpdateAutogiroInput {
    approved: boolean
    customerId: number
    requestedAccountNumber?: string | null
    requestedClearingNumber?: string | null
}

/**
 * Response wrapper for updateAutogiro mutation.
 */
export interface UpdateAutogiroResponse {
    updateAutogiro: {
        autogiro: {
            accountNumber: string | null
            approved: boolean
            changeRequested: boolean
            changeRequestedAt: string | null
            clearingNumber: string | null
        }
    }
}
